import { useParams } from 'react-router-dom'
import { submitMilestoneStep } from '../../../services/api/collaboration'
import { Gig, Milestone, MilestoneStep } from '../../../types/collaborations/CollaborationTypes'
import withSlideUpModal from '../../../utils/hoc/withSlideUpModal'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch, RootState } from '../../../redux-stores/store'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { useMediaQuery } from 'react-responsive'
import { isDesktopMediaQuery } from '../../../constants/constants'
import { fetchInfluencerContent, fetchSocialPostsThunk, markExpired, setLoading } from '../../../redux-stores/slices/influencerContentSlice'
import { fetchLatestInfluencerMedia, fetchLatestInfluencerSocialPosts } from '../../../services/api/influencer'
import { getErrorMessage, retryUntil, textToTitleCase } from '../../../utils/helpers'
import { Bounce, toast } from 'react-toastify'
import { MediaProductType, MediaType } from '../../../types/influencer/influencerTypes'
import InfiniteScroll from 'react-infinite-scroll-component'
import InfluencerPostCard from '../../influencer/InfluencerPostCard'
import Badge from '../../badges/Badge'
import Skeleton from 'react-loading-skeleton'
import SocialPostCard from '../../influencer/SocialPostCard'


interface SocialPostSelectionProps {
    step: MilestoneStep,
    gigs: Gig[]
    activeMilestone: Milestone
    isActive: boolean,
    onClose: (refetch?: boolean) => void,
    setSelectedMedia: (post: any) => void
}

const SocialPostSelection: React.FC<SocialPostSelectionProps> = ({ activeMilestone, onClose, step }) => {

    const { campaignId } = useParams();

    const dispatch: AppDispatch = useDispatch();

    const [submitting, setSubmitting] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
    const [fetchingLatestMedia, setFetchingLatestMedia] = useState(false);

    const collaboration = useSelector((state: RootState) => state.collaborationDetails.collaborations[campaignId!]?.collaborationData);
    const socialAccount = collaboration?.collaboration?.social_account;

    const mediaProductType = useMemo(() => {
        switch (step.gig_step.attributes.media_product_type) {
            case 'REELS':
                return MediaProductType.REEL
            case 'STORY':
                return MediaProductType.STORY
            case 'VIDEO':
                return MediaProductType.VIDEO
            default:
                return MediaProductType.FEED
        }
    }, [step.gig_step.attributes.media_product_type])

    const [selectedPosts, setSelectedPosts] = useState<string[]>([]);
    const [alreadySelectedPosts, setAlreadySelectedPosts] = useState<string[]>([]);


    const [approvedPosts, setApprovedPosts] = useState<string[]>([]);
    const [rejectedPosts, setRejectedPosts] = useState<string[]>([]);

    const data = useSelector(
        (state: RootState) => state.influencerContent.social_posts[socialAccount?.uuid]?.[mediaProductType]
    );

    const posts = data ? data.items.filter((post) => {
        return (
            moment((post.posted_on ?? ''), "MMMM D, YYYY, h:mm a").isAfter(moment((collaboration?.collaboration?.created_at ?? ''))) ||
            approvedPosts.includes(post.uuid) ||
            rejectedPosts.includes(post.uuid) ||
            selectedPosts.includes(post.uuid)
        )
    }) : [];


    useEffect(() => {
        if(data){
            setLastFetchedAt(data.last_fetched_at_server);
        }
    }, [data?.last_fetched_at_server])

    const has_more = data ? data.has_more : true;
    const page = data ? data.page : 1;

    const loading = useSelector((state: RootState) => state.influencerContent.loading);

    const isDesktop = useMediaQuery(isDesktopMediaQuery)


    const fetchCollaborationMedia = useCallback(async (pageNumber: number, force = false) => {
        if (!campaignId || !socialAccount) return null;
        try {
            const latestData = await dispatch(fetchSocialPostsThunk({ socialAccountId: socialAccount.uuid, mediaProductType, page: pageNumber, force })).unwrap();
            return latestData.media;
        } catch (error) {
            return null;
        }
    }, [campaignId, dispatch, mediaProductType, socialAccount]);



    useEffect(() => {
        if (!campaignId) return;
        fetchCollaborationMedia(1);
    }, [mediaProductType, campaignId, fetchCollaborationMedia]);

    const nextPage = () => {
        if (has_more && !loading) {
            const nextPageNumber = page + 1;
            fetchCollaborationMedia(nextPageNumber)
        }
    }

    useEffect(() => {
        if (collaboration) {
            let selected_posts: string[] = [], rejected_posts: string[] = [], approved_posts: string[] = [];
            if (collaboration?.collaboration && Array.isArray(collaboration.collaboration.posts)) {
                for (let post of collaboration.collaboration.posts) {
                    if(post.social_post?.type !== mediaProductType) continue;
                    if (post.status === 'approved') approved_posts.push(post.social_post.uuid);
                    if (post.status === 'rejected') rejected_posts.push(post.social_post.uuid);
                    if (post.status === 'pending') selected_posts.push(post.social_post.uuid);
                }
            }
            setSelectedPosts(selected_posts);
            setAlreadySelectedPosts(selected_posts);
            setApprovedPosts(approved_posts);
            setRejectedPosts(rejected_posts);
        }
    }, [collaboration])


    const fetchLatestMedia = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        if (fetchingLatestMedia || !socialAccount) return;
        try {
            setFetchingLatestMedia(true);
            const loadingToastId = toast("Fetching latest media", {
                position: "top-center",
                autoClose: 32000,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                hideProgressBar: false,
                theme: "colored",
                transition: Bounce,
                type: 'info',
                isLoading: true,
                closeButton: false
            })
            await fetchLatestInfluencerSocialPosts(socialAccount!.uuid);
            await retryUntil(async () => {
                const prev = lastFetchedAt;
                const latestData = await fetchCollaborationMedia(1, true);
                const latest = (latestData?.last_fetched_at ?? null);
                return latest !== prev;
            });

            toast.update(loadingToastId, {
                type: 'success',
                isLoading: false,
                render:  "Showing latest media",
                autoClose: 2000,
            });
        } catch (error: any) {
            console.log(error);
            toast.error(getErrorMessage(error));
        } finally {
            setFetchingLatestMedia(false)
        }
    }

    const selectPost = (postId: string) => {
        if (approvedPosts.includes(postId) || rejectedPosts.includes(postId) || alreadySelectedPosts.includes(postId)) return;

        if (selectedPosts.includes(postId)) {
            setSelectedPosts([...selectedPosts.filter((pid) => pid !== postId)]);
        } else {
            setSelectedPosts([...selectedPosts, postId])
        }
    }

    const newlySelectedPosts = useMemo(() => {
        return selectedPosts.filter((selectedPost) => !alreadySelectedPosts.includes(selectedPost))
    }, [selectedPosts, alreadySelectedPosts])

    const onSubmit = async () => {
        if (!campaignId || !newlySelectedPosts.length) return;
        try {
            setSubmitting(true);
            await submitMilestoneStep(campaignId, { step: step.gig_step.slug, social_posts: newlySelectedPosts });
            onClose(true);
        } catch (error: any) {
            console.log(error);
            toast.error(error?.response?.data?.message ?? "Unknown error occurred.")
        } finally {
            setSubmitting(false);
        }
    }


    const getMediaProductType = (multiple: boolean = false) => {
        switch (mediaProductType) {
            case MediaProductType.REEL:
                return multiple ? 'reels' : 'reel';
            case MediaProductType.STORY:
                return multiple ? 'stories' : 'story';
            default:
                return multiple ? 'posts' : 'post';
        }
    }

    return (
        <React.Fragment>
            <div className="">
                <div className="post-selection-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <span>All {textToTitleCase(getMediaProductType(true))}</span>
                            <p className='m-0'>{activeMilestone.content_media_types[mediaProductType]} {getMediaProductType(Number(activeMilestone.content_media_types[mediaProductType]) > 1)} to be uploaded</p>
                        </div>

                        <button type="button" className="btn btn-xxs ms-2 btn-primary rounded-sm" title="Fetch Latest Media" onClick={fetchLatestMedia}>
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>
                </div>
                <div style={{ height: '56vh', overflowY: 'auto' }} id="scrollableContent">
                    <InfiniteScroll
                        className="row gx-2"
                        hasMore={has_more}
                        next={nextPage}
                        dataLength={posts.length}
                        loader={<></>}
                        scrollableTarget='scrollableContent'
                    >
                        {posts.map((post, postIndex) => (
                            <div className="col-6 p-1 position-relative" key={postIndex} style={{ position: 'relative' }} >
                                <SocialPostCard newStyle={true} post={post} onPostClick={selectPost} />
                                {
                                    !approvedPosts.includes(post.uuid) && !rejectedPosts.includes(post.uuid) &&
                                    <div className='post-check-wrapper'>
                                        <input type="checkbox" className="form-check-input text-white bg-transparent post-select-input" style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            border: "2px solid white"
                                        }}
                                            disabled={approvedPosts.includes(post.uuid) || rejectedPosts.includes(post.uuid) || alreadySelectedPosts.includes(post.uuid)}
                                            checked={selectedPosts.includes(post.uuid)}
                                            onChange={() => selectPost(post.uuid)}
                                        />
                                    </div>
                                }
                                {
                                    (approvedPosts.includes(post.uuid) || rejectedPosts.includes(post.uuid) || alreadySelectedPosts.includes(post.uuid)) &&
                                    <div style={{ position: "absolute", top: "20px", marginLeft: 'auto', marginRight: 'auto', left: 0, right: 0, textAlign: 'center' }}>
                                        {
                                            approvedPosts.includes(post.uuid) &&
                                            <Badge variant='success'><i className="fa fa-check me-1"></i>Approved</Badge>
                                        }
                                        {
                                            rejectedPosts.includes(post.uuid) &&
                                            <Badge variant="danger"><i className="fa fa-times me-1"></i> Declined</Badge>
                                        }
                                        {
                                            alreadySelectedPosts.includes(post.uuid) &&
                                            <Badge variant="warning"><i className="fa fa-clock me-1"></i> Pending</Badge>
                                        }
                                    </div>
                                }
                            </div>
                        ))}
                    </InfiniteScroll>
                    {
                        loading &&
                        <div className="row m-0">
                            {
                                Array.from({ length: 4 }).map((_, i) => {
                                    return (
                                        <div className="col-6 p-1" key={i}>
                                            <Skeleton style={{ width: '100%', height: isDesktop ? '270px' : '145px', padding: 0, margin: 0, borderRadius: '10px' }} />
                                        </div>
                                    )
                                })
                            }
                        </div>
                    }
                    {
                        !loading && !posts.length &&
                        <span className="w-100 d-flex justify-content-center align-items-center" style={{ height: 'calc(100% - 60px)' }}>No media found since collaboration started!</span>
                    }
                </div>
            </div>
            <div className="milestone-step-actions">
                <button className="theme-btn theme-btn-primary" disabled={newlySelectedPosts.length === 0 || submitting || fetchingLatestMedia} onClick={onSubmit}>Send for Approval</button>
            </div>
        </React.Fragment>

    )
}

const SocialPostSelectionModal: React.FC<SocialPostSelectionProps> = (props) => {
    const { step } = props
    let contentType = "Post";
    switch (step.gig_step.attributes.media_product_type) {
        case 'REELS':
            contentType = 'Reel';
            break;
        case 'STORY':
            contentType = 'Story';
            break;
        case 'VIDEO':
            contentType = 'Video';
            break;
        default:
            contentType = "Post"
    }
    const title = `Upload Live ${contentType}`

    const ModalComponent = useMemo(() => withSlideUpModal(SocialPostSelection, title), [title])
    return <ModalComponent {...props} />
}


export default SocialPostSelectionModal;